import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { DirtyCardSummary } from '@/features/patient-record/lib/dirtyRegistry';

interface UnsavedChangesDialogProps {
  readonly open: boolean;
  /**
   * Optional list of what is unsaved — card/form label plus the NAMES (never values) of the fields
   * that differ. Shown so the user knows what they'd lose. Omit for a generic message.
   */
  readonly summaries?: readonly DirtyCardSummary[];
  /** Commit the unsaved work, then proceed with whatever was interrupted (navigation / dismiss). */
  readonly onSave: () => void;
  /** Throw the unsaved work away, then proceed. */
  readonly onDiscard: () => void;
  /** Abort the interruption and return the user to their edit. */
  readonly onKeepEditing: () => void;
  readonly saving?: boolean;
  /**
   * Whether the unsaved work can be saved right now (PTV2-1301). When false — a required field is
   * still empty — Save is disabled and a warning is shown, so the only ways out are Discard or Keep
   * editing. Defaults to true.
   */
  readonly canSave?: boolean;
}

/**
 * PTV2-1301 — the shared "you have unsaved changes" decision prompt.
 *
 * Three-way, and deliberately un-dismissable (Escape / outside-click are swallowed): a stray click
 * must not be read as either Save or Discard. Raised from two places — a create/edit dialog's own
 * dismiss guard, and the section-navigation blocker — both of which need the same Save / Discard /
 * Keep-editing choice. Field NAMES only, no values (they may be PHI and the dialog can be
 * screenshotted), matching the pedigree's node-level prompt.
 */
export function UnsavedChangesDialog({
  open,
  summaries,
  onSave,
  onDiscard,
  onKeepEditing,
  saving = false,
  canSave = true,
}: UnsavedChangesDialogProps): ReactElement {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Only honour a programmatic close→open(false) as "keep editing"; the swallowed
        // Escape/outside handlers below mean this fires only via the buttons' own flow.
        if (!next) onKeepEditing();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(event) => { event.preventDefault(); }}
        onPointerDownOutside={(event) => { event.preventDefault(); }}
        onInteractOutside={(event) => { event.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>
            {t('patient.unsavedChanges.title', { defaultValue: 'Unsaved changes' })}
          </DialogTitle>
          <DialogDescription>
            {t('patient.unsavedChanges.body', {
              defaultValue: 'You have unsaved data. Save it or discard it before leaving?',
            })}
          </DialogDescription>
        </DialogHeader>

        {!canSave && (
          <p className="text-sm font-medium text-destructive">
            {t('patient.unsavedChanges.cannotSave', {
              defaultValue: 'Cannot save — some required fields are empty.',
            })}
          </p>
        )}

        {summaries !== undefined && summaries.length > 0 && (
          <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-muted/30 p-3">
            <ul className="space-y-2 text-sm">
              {summaries.map((s) => (
                <li key={s.label}>
                  <span className="font-medium text-foreground">{s.label}</span>
                  {s.fields.length > 0 && (
                    <span className="text-muted-foreground"> — {s.fields.join(', ')}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" onClick={onKeepEditing} disabled={saving}>
            {t('patient.unsavedChanges.keepEditing', { defaultValue: 'Keep editing' })}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onDiscard} disabled={saving}>
              {t('patient.unsavedChanges.discard', { defaultValue: 'Discard' })}
            </Button>
            <Button type="button" onClick={onSave} disabled={saving || !canSave}>
              {t('patient.unsavedChanges.save', { defaultValue: 'Save' })}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
